import React, { useState, useEffect } from 'react';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { StudyPlanContextInterface, MathAndVerbalMix } from './types';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';

const StudyPlanContext = React.createContext<StudyPlanContextInterface | null>(null);

export const useStudyPlanContext = () => {
  const context = React.useContext(StudyPlanContext);
  if (context === undefined) {
    throw new Error('You must wrap your page with <StudyPlanProvider /> in order to useStudyPlanContext().');
  }
  return context;
};

export const StudyPlanProvider: React.FC = ({ children }) => {
  const [studyPlanState, setStudyPlanState] = useState({
    name: '',
    testDate: '',
    recommendedWeeklyHours: 12,
    hoursStudiedThisWeek: 0,
    prioritizedCheckpoint: {
      type: '',
      bannerSlug: '',
      number: 0
    },
    toDoItems: [],
    completedItems: [],
    currentAndUpcomingCheckpoints: [],
    notes: [],
    settings: {
      mathAndVerbalMix: 'balanced' as MathAndVerbalMix,
      shouldIncludeWriting: false
    }
  });
  const [currentAssessment, setCurrentAssessment] = useState({
    type: '',
    bannerSlug: '',
    number: 0
  });
  const [loading, setLoading] = useState(true);
  const authToken = useAuthenticityToken();

  useEffect(() => {
    fetchStudyPlan();
  }, []);

  const fetchStudyPlan = React.useCallback(() => {
    fetch(paths.api.studyPlanData).then((newState) => {
      setStudyPlanState(newState);
      setLoading(false);

      // The assessment might be the prioritizedCheckpoint, in the currentAndUpcomingCheckpoints,
      // or not present
      if (newState.prioritizedCheckpoint && newState.prioritizedCheckpoint.type == 'Assessment') {
        setCurrentAssessment(newState.prioritizedCheckpoint);
      } else if (newState.currentAndUpcomingCheckpoints) {
        setCurrentAssessment(
          newState.currentAndUpcomingCheckpoints.find((checkpoint) => {
            return checkpoint.type == 'Assessment' && checkpoint.notAvailableYet != true;
          })
        );
      }
    });
  }, [studyPlanState]);

  const addATopic = (segmentId: number) => {
    return fetch(paths.api.studyPlanItems(), {
      method: 'POST',
      body: JSON.stringify({
        studyPlanItem: {
          contentBlockId: segmentId
        }
      })
    }).then((newState) => {
      setStudyPlanState(newState);
    });
  };

  const moveToEnd = (item) => {
    fetch(paths.api.moveStudyPlanItemToEnd(item.id), {
      method: 'PATCH'
    }).then((newState) => {
      setStudyPlanState(newState);
    });
  };

  const deferAssessment = () => {
    if (currentAssessment && currentAssessment.number == 1) {
      setLoading(true);

      fetch(paths.api.deferFirstAssessment, {
        method: 'POST',
        body: JSON.stringify({})
      }).then((newState) => {
        setStudyPlanState(newState);
        setLoading(false);
      });
    }
  };

  const dismissRefresh = () => {
    if (studyPlanState.prioritizedCheckpoint && studyPlanState.prioritizedCheckpoint.bannerSlug) {
      fetch(paths.api.dismissBanner(studyPlanState.prioritizedCheckpoint.bannerSlug), {
        method: 'PUT'
      })
        .then(() => fetch(paths.api.studyPlanData))
        .then((newState) => {
          setStudyPlanState(newState);
        });
    }
  };

  const markComplete = (item) => {
    fetch(paths.api.markStudyPlanItemComplete(item.id), {
      method: 'PATCH'
    }).then((newState) => {
      setStudyPlanState(newState);
    });
  };

  const modifyAndRefreshPlan = (newSettings) => {
    setLoading(true);
    // Immediately update the settings state to give user feedback
    setStudyPlanState({ ...studyPlanState, settings: newSettings });

    fetch(paths.api.createAdaptivePrepStudyPlan, {
      method: 'POST',
      body: JSON.stringify({
        settings: newSettings
      })
    }).then((newState) => {
      setStudyPlanState(newState);
      setLoading(false);
    });
  };

  const refreshPlan = () => {
    setLoading(true);

    fetch(paths.api.createAdaptivePrepStudyPlan, {
      method: 'POST'
    }).then((newState) => {
      setStudyPlanState(newState);
      setLoading(false);
    });
  };

  const providerValue = React.useMemo(
    () => ({
      studyPlanState,
      currentAssessment,
      loading,
      authToken,
      addATopic,
      moveToEnd,
      deferAssessment,
      dismissRefresh,
      markComplete,
      modifyAndRefreshPlan,
      refreshPlan
    }),
    [
      studyPlanState,
      currentAssessment,
      loading,
      authToken,
      addATopic,
      moveToEnd,
      deferAssessment,
      dismissRefresh,
      markComplete,
      modifyAndRefreshPlan,
      refreshPlan
    ]
  );

  return <StudyPlanContext.Provider value={providerValue}>{children}</StudyPlanContext.Provider>;
};
